// @flow

import React from "react";
import { Form } from "react-final-form";

import { Button, FormRow, FieldControl, PasswordInput } from "buba-components";

import * as validators from "../../utils/validators";
import { errors as e } from "../../utils/errors";
import { isFormSubmitting } from "../../utils/helpers";

import {
  REFRESH_PASSWORD_OLD_PASSWORD,
  REFRESH_PASSWORD_PASSWORD,
  REFRESH_PASSWORD_PASSWORD_CONFIRMATION
} from "./formFields";

type ChangePasswordFormProps = {
  submitForm: (errorResponse: {
    data: { errors?: { type: string }[] }
  }) => ({ oldPassword: string, password: string, passwordConfirmation: string }) => void,
  passwordRegex: RegExp,
  passwordError: string
};

const mapFormErrors =
  (setIsOldPasswordErrorShowed, setIsPasswordErrorShowed) =>
  (errorResponse: { data: { errors?: { type: string }[] } }) => {
    const { errors } = errorResponse.data;

    if (!errors) {
      return;
    }

    errors.forEach(error => {
      switch (error.type) {
        case "invalid_password":
          setIsOldPasswordErrorShowed(true);
          break;
        case "password_already_used":
          setIsPasswordErrorShowed(true);
          break;
        default:
          break;
      }
    });
  };

const ChangePasswordForm = ({
  submitForm,
  passwordRegex,
  passwordError
}: ChangePasswordFormProps) => {
  const [isOldPasswordErrorShowed, setIsOldPasswordErrorShowed] = React.useState(false);
  const [isPasswordErrorShowed, setIsPasswordErrorShowed] = React.useState(false);

  return (
    <Form
      onSubmit={submitForm(mapFormErrors(setIsOldPasswordErrorShowed, setIsPasswordErrorShowed))}
    >
      {({ form }) => (
        <form onSubmit={() => false}>
          <FormRow>
            <FieldControl
              name={REFRESH_PASSWORD_OLD_PASSWORD}
              caption={() => "Vorheriges Passwort"}
              validate={validators.required(e.requiredField())}
              validationMessage={isOldPasswordErrorShowed ? e.invalidPassword() : null}
            >
              {({ input, meta }) => (
                <PasswordInput
                  input={input}
                  meta={meta}
                  placeholder="Geben Sie Ihr Vorheriges Passwort an..."
                />
              )}
            </FieldControl>
            <FieldControl
              name={REFRESH_PASSWORD_PASSWORD}
              caption={() => "Neues Passwort"}
              validate={validators.combineValidators(
                validators.required(e.requiredField()),
                validators.isPassword(passwordRegex, passwordError)
              )}
              validationMessage={isPasswordErrorShowed ? e.isAlreadyUsedPassword() : null}
            >
              {({ input }) => (
                <PasswordInput input={input} placeholder="Geben Sie Ihr Neues Passwort an..." />
              )}
            </FieldControl>
            <FieldControl
              name={REFRESH_PASSWORD_PASSWORD_CONFIRMATION}
              caption={() => "Neues Passwort Wiederholen"}
              validate={validators.combineValidators(
                validators.required(e.requiredField()),
                validators.equalToField(REFRESH_PASSWORD_PASSWORD, e.passwordMatch())
              )}
            >
              {({ input }) => (
                <PasswordInput
                  input={input}
                  placeholder="Geben Sie erneut Ihr Neues Passwort an..."
                />
              )}
            </FieldControl>
          </FormRow>
          <Button
            data-testid="password-change-button"
            isDisabled={!form.getState().valid || isFormSubmitting(form)}
            onClick={() => {
              setIsOldPasswordErrorShowed(false);
              setIsPasswordErrorShowed(false);
              form.submit();
            }}
          >
            Passwort festlegen
          </Button>
        </form>
      )}
    </Form>
  );
};

export default ChangePasswordForm;
